<template>
  <div class="element-style-wrapper">
    <component v-if="injectedCss" :is="'style'" class="element-style-wrapper__injected">{{ injectedCss }}</component>
    <!-- When we have both before and after markup (wrapper pattern like <div>{content}</div>) -->
    <component
      v-if="hasWrapperMarkup && wrapperTagName"
      :is="wrapperTagName"
      v-bind="wrapperAttributes"
      class="element-style-wrapper__wrapper"
    >
      <slot />
    </component>
    <!-- Fallback for non-wrapper pattern or complex HTML -->
    <template v-else>
      <div v-if="sanitizedMarkupBefore" class="element-style-wrapper__before" v-html="sanitizedMarkupBefore" />
      <div class="element-style-wrapper__content">
        <slot />
      </div>
      <div v-if="sanitizedMarkupAfter" class="element-style-wrapper__after" v-html="sanitizedMarkupAfter" />
    </template>
  </div>
</template>

<script setup lang="ts">
import { sanitizeElementStyleWrapperHtml } from '@/common/utils/html-sanitizer'
import { computed } from 'vue'

const props = defineProps<{
  cssText?: string
  beforeHtml?: string
  afterHtml?: string
}>()

// Use CSS directly from props (already extracted in parseElementStyleWrapper)
// Add !important to override component default styles (like PrimeVue)
const injectedCss = computed(() => {
  const css = props.cssText ?? ''
  if (!css) return ''

  // Add !important before each semicolon if not already present
  return css.split(';').map(rule => {
    if (!rule.trim()) return ''
    if (rule.includes('!important')) return rule + ';'
    return rule.trim() + ' !important;'
  }).join('')
})

/**
 * Parse simple wrapper tag (like <div class="pd-banners">) into tag name and attributes.
 */
function parseWrapperTag(html: string): { tagName: string; attributes: Record<string, string> } | null {
  const match = /^<([a-z]+)((?:\s+[a-z-]+(?:="[^"]*")?)*)\s*>$/i.exec(html.trim())
  if (!match) return null

  const tagName = match[1]
  const attrsStr = match[2] || ''
  const attributes: Record<string, string> = {}

  // Parse attributes like class="pd-banners"
  const attrRegex = /([a-z-]+)(?:="([^"]*)"|)/gi
  let attrMatch: RegExpExecArray | null
  while ((attrMatch = attrRegex.exec(attrsStr)) !== null) {
    attributes[attrMatch[1]] = attrMatch[2] || ''
  }

  return { tagName, attributes }
}

/**
 * Check if we have a simple wrapper pattern: opening tag + closing tag.
 */
const hasWrapperMarkup = computed(() => {
  const before = (props.beforeHtml ?? '').trim()
  const after = (props.afterHtml ?? '').trim()

  if (!before || !after) return false

  const openingTag = parseWrapperTag(before)
  if (!openingTag) return false

  const closingTagMatch = /^<\/([a-z]+)>$/i.exec(after)
  if (!closingTagMatch) return false

  return openingTag.tagName === closingTagMatch[1]
})

const wrapperTagName = computed(() => {
  if (!hasWrapperMarkup.value) return null
  const parsed = parseWrapperTag((props.beforeHtml ?? '').trim())
  return parsed?.tagName ?? null
})

const wrapperAttributes = computed(() => {
  if (!hasWrapperMarkup.value) return {}
  const parsed = parseWrapperTag((props.beforeHtml ?? '').trim())
  return parsed?.attributes ?? {}
})

/**
 * Sanitize markup from element style wrapper.
 */
function sanitizeElementWrapperMarkup(html: string): string {
  if (!html || typeof html !== 'string') return ''

  // Simple wrapper pattern: opening/closing tag only (like <div class="pd-banners">)
  const isSimpleWrapper = /^<[a-z]+[^>]*>$/.test(html.trim()) || /^<\/[a-z]+>$/.test(html.trim())

  if (isSimpleWrapper) {
    return html
  }

  return sanitizeElementStyleWrapperHtml(html)
}

const sanitizedMarkupBefore = computed(() =>
  sanitizeElementWrapperMarkup(props.beforeHtml ?? ''),
)
const sanitizedMarkupAfter = computed(() =>
  sanitizeElementWrapperMarkup(props.afterHtml ?? ''),
)
</script>

<style scoped lang="scss">
.element-style-wrapper {
  display: contents;
}

.element-style-wrapper__content {
  display: contents;
}
</style>
